import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Page,
  Card,
  IndexTable,
  IndexFilters,
  useSetIndexFiltersMode,
  Badge,
  Text,
  Avatar,
  Banner,
  useIndexResourceState,
  type IndexFiltersProps,
  type TabProps,
} from '@shopify/polaris';
import {
  userService,
  type UserListItem,
  type UserStatus,
  type AuthMethod,
} from '../../services/user.service';

const STATUS_OPTIONS: { label: string; value: UserStatus }[] = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'INACTIVE' },
  { label: 'Pending Profile', value: 'PENDING_PROFILE' },
];

const AUTH_METHOD_OPTIONS: { label: string; value: AuthMethod }[] = [
  { label: 'Google', value: 'GOOGLE' },
  { label: 'LinkedIn', value: 'LINKEDIN' },
  { label: 'Phone OTP', value: 'PHONE_OTP' },
  { label: 'Email OTP', value: 'EMAIL_OTP' },
];

function getStatusBadge(status: UserStatus) {
  const toneMap: Record<UserStatus, 'success' | 'critical' | 'warning' | undefined> = {
    ACTIVE: 'success',
    INACTIVE: 'critical',
    PENDING_PROFILE: 'warning',
  };

  const labelMap: Record<UserStatus, string> = {
    ACTIVE: 'Active',
    INACTIVE: 'Inactive',
    PENDING_PROFILE: 'Pending',
  };

  return <Badge tone={toneMap[status]}>{labelMap[status]}</Badge>;
}

function getAuthMethodLabel(method: AuthMethod): string {
  const labels: Record<AuthMethod, string> = {
    GOOGLE: 'Google',
    LINKEDIN: 'LinkedIn',
    PHONE_OTP: 'Phone',
    EMAIL_OTP: 'Email',
  };
  return labels[method];
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function UserListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);

  // Filters
  const [queryValue, setQueryValue] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<UserStatus | ''>('');
  const [authMethodFilter, setAuthMethodFilter] = useState<AuthMethod | ''>('');
  const [page, setPage] = useState(1);

  const { mode, setMode } = useSetIndexFiltersMode();
  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(users as unknown as {[key: string]: unknown}[]);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await userService.list({
        page,
        limit: 20,
        search: queryValue || undefined,
        status: statusFilter || undefined,
        authMethod: authMethodFilter || undefined,
      });
      setUsers(result.data);
      setTotal(result.meta.total);
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, queryValue, statusFilter, authMethodFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleFiltersQueryChange = useCallback((value: string) => {
    setQueryValue(value);
    setPage(1);
  }, []);

  const handleQueryValueRemove = useCallback(() => {
    setQueryValue('');
    setPage(1);
  }, []);

  const handleStatusFilterChange = useCallback((value: string[]) => {
    setStatusFilter((value[0] as UserStatus) || '');
    setPage(1);
  }, []);

  const handleAuthMethodFilterChange = useCallback((value: string[]) => {
    setAuthMethodFilter((value[0] as AuthMethod) || '');
    setPage(1);
  }, []);

  const handleFiltersClearAll = useCallback(() => {
    setQueryValue('');
    setStatusFilter('');
    setAuthMethodFilter('');
    setPage(1);
  }, []);

  const filters: IndexFiltersProps['filters'] = [
    {
      key: 'status',
      label: 'Status',
      filter: (
        <select
          value={statusFilter}
          onChange={(e) => handleStatusFilterChange([e.target.value])}
          style={{ width: '100%', padding: '8px' }}
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ),
      shortcut: true,
    },
    {
      key: 'authMethod',
      label: 'Auth Method',
      filter: (
        <select
          value={authMethodFilter}
          onChange={(e) => handleAuthMethodFilterChange([e.target.value])}
          style={{ width: '100%', padding: '8px' }}
        >
          <option value="">All Methods</option>
          {AUTH_METHOD_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ),
      shortcut: true,
    },
  ];

  const appliedFilters: IndexFiltersProps['appliedFilters'] = [];
  if (statusFilter) {
    appliedFilters.push({
      key: 'status',
      label: `Status: ${STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label}`,
      onRemove: () => setStatusFilter(''),
    });
  }
  if (authMethodFilter) {
    appliedFilters.push({
      key: 'authMethod',
      label: `Auth: ${AUTH_METHOD_OPTIONS.find((o) => o.value === authMethodFilter)?.label}`,
      onRemove: () => setAuthMethodFilter(''),
    });
  }

  const tabs: TabProps[] = [
    { id: 'all', content: 'All', accessibilityLabel: 'All users' },
    { id: 'active', content: 'Active', accessibilityLabel: 'Active users' },
    { id: 'inactive', content: 'Inactive', accessibilityLabel: 'Inactive users' },
  ];

  const [selectedTab, setSelectedTab] = useState(0);

  const handleTabChange = useCallback((tabIndex: number) => {
    setSelectedTab(tabIndex);
    const tabStatuses: Record<number, UserStatus | ''> = {
      0: '',
      1: 'ACTIVE',
      2: 'INACTIVE',
    };
    setStatusFilter(tabStatuses[tabIndex] || '');
    setPage(1);
  }, []);

  const rowMarkup = users.map((user, index) => (
    <IndexTable.Row
      id={user.id}
      key={user.id}
      selected={selectedResources.includes(user.id)}
      position={index}
      onClick={() => navigate(`/users/${user.id}`)}
    >
      <IndexTable.Cell>
        <Avatar
          customer
          size="sm"
          name={user.name || user.email}
          source={user.profilePicture || undefined}
        />
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" fontWeight="bold" as="span">
          {user.name || 'No name'}
        </Text>
        <Text variant="bodySm" tone="subdued" as="p">
          {user.email}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {user.companyName && (
          <>
            <Text variant="bodyMd" as="span">
              {user.companyName}
            </Text>
            {user.designation && (
              <Text variant="bodySm" tone="subdued" as="p">
                {user.designation}
              </Text>
            )}
          </>
        )}
        {!user.companyName && (
          <Text variant="bodySm" tone="subdued" as="span">
            -
          </Text>
        )}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge>{getAuthMethodLabel(user.authMethod)}</Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>{getStatusBadge(user.status)}</IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd" as="span">
          {user._count.inquiries}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>{formatDate(user.createdAt)}</IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Page title="Users" fullWidth>
      {error && (
        <Banner tone="critical" onDismiss={() => setError('')}>
          {error}
        </Banner>
      )}

      <Card padding="0">
        <IndexFilters
          queryValue={queryValue}
          queryPlaceholder="Search by name, email, company..."
          onQueryChange={handleFiltersQueryChange}
          onQueryClear={handleQueryValueRemove}
          tabs={tabs}
          selected={selectedTab}
          onSelect={handleTabChange}
          filters={filters}
          appliedFilters={appliedFilters}
          onClearAll={handleFiltersClearAll}
          mode={mode}
          setMode={setMode}
          canCreateNewView={false}
        />
        <IndexTable
          resourceName={{ singular: 'user', plural: 'users' }}
          itemCount={users.length}
          selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
          onSelectionChange={handleSelectionChange}
          headings={[
            { title: '' },
            { title: 'Name' },
            { title: 'Company' },
            { title: 'Auth' },
            { title: 'Status' },
            { title: 'Inquiries' },
            { title: 'Joined' },
          ]}
          loading={loading}
          pagination={{
            hasNext: page * 20 < total,
            hasPrevious: page > 1,
            onNext: () => setPage((p) => p + 1),
            onPrevious: () => setPage((p) => p - 1),
          }}
        >
          {rowMarkup}
        </IndexTable>
      </Card>
    </Page>
  );
}
